import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Components
import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';

// Utils
import { getProjectCode } from '../../utils/auth';
import { ProjectDetail } from '../../utils/commonUtils';
import { createProduct } from '../../utils/product';
import { FormData, FormErrors, ProductFormUtils } from '../../utils/productCommon';
import { getBrandListFlat, FlatBrandNode } from '../../utils/brand';
import { 
  Home,
  Package,
  Save,
  ArrowLeft,
  X,
  Check,
  Info,
  DollarSign,
  Gift,
  Settings,
  FileText,
  RefreshCw
} from 'lucide-react';

const ProductCreate: React.FC = () => {
  // Router hooks
  const navigate = useNavigate();

  // UI state
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('product-list');
  
  // Form state
  const [formData, setFormData] = useState<FormData>(ProductFormUtils.getDefaultFormData());

  // Form errors
  const [errors, setErrors] = useState<FormErrors>({});

  // API state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Project state
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);

  // Brand state
  const [brands, setBrands] = useState<FlatBrandNode[]>([]);
  const [isLoadingBrands, setIsLoadingBrands] = useState(false);

  // Check if screen is mobile size
  ProductFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  // 프로젝트 정보 로드
  useEffect(() => {
    const projectInfo = ProductFormUtils.loadProjectDetail();
    if (projectInfo) {
      setProjectDetail(projectInfo);
    }
  }, []);

  // 브랜드 목록 조회
  useEffect(() => {
    if (!projectDetail) return;
    const projectCode = projectDetail.code || getProjectCode();
    if (!projectCode) return;
    let cancelled = false;
    setIsLoadingBrands(true);
    getBrandListFlat(projectCode)
      .then((res) => {
        if (!cancelled) {
          if (res?.brands) setBrands(res.brands);
          else setBrands([]);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('브랜드 목록 조회 오류:', err);
          setBrands([]);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingBrands(false);
      });
    return () => { cancelled = true; };
  }, [projectDetail?.code]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    ProductFormUtils.handleInputChange(e, setFormData, setErrors);
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors = ProductFormUtils.validateCreateForm(formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Reset form
  const handleReset = () => {
    setFormData(ProductFormUtils.getDefaultFormData());
    setErrors({});
    setSubmitError(null);
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    await ProductFormUtils.handleCreateSubmit(
      e,
      formData,
      validateForm,
      setIsSubmitting,
      setSubmitError,
      getProjectCode,
      createProduct,
      navigate
    );
  };

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Sidebar */}
      {projectDetail && (
        <ProjectSidebar
          isDesktopSidebarOpen={isDesktopSidebarOpen}
          setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          activeMenu={activeMenu}
          setActiveMenu={setActiveMenu}
          projectDetail={projectDetail}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        {projectDetail && (
          <Topbar
            title="상품 생성"
            subtitle={projectDetail.name}
            isMobileSidebarOpen={isMobileSidebarOpen}
            setIsMobileSidebarOpen={setIsMobileSidebarOpen}
            isProfileDropdownOpen={isProfileDropdownOpen}
            setIsProfileDropdownOpen={setIsProfileDropdownOpen}
            showMobileLogo={true}
            projectDetail={projectDetail}
          />
        )}

        {/* Create Product Content */}
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-4xl mx-auto">
            {/* Back Button */}
            <div className="mb-6">
              <button
                onClick={() => navigate('/products')}
                className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                상품 목록으로
              </button>
            </div>

            {/* Error Message */}
            {submitError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">오류가 발생했습니다</h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{submitError}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit}>
              {/* Basic Information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Info className="w-5 h-5 mr-2" />
                  기본 정보
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Brand Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      브랜드
                    </label>
                    <select
                      name="brandCode"
                      value={formData.brandCode || ''}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border ${errors.brandCode ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      disabled={isLoadingBrands}
                    >
                      <option value="">브랜드를 선택하세요</option>
                      {brands.map((brand) => {
                        const nbsp = '\u00A0';
                        const indent = (nbsp + nbsp).repeat(brand.depth);
                        const prefix = brand.depth > 0 ? 'ㄴ ' : '';
                        return (
                          <option key={brand.code} value={brand.code}>
                            {`${indent}${prefix}${brand.name} (${brand.code})`}
                          </option>
                        );
                      })}
                    </select>
                    {errors.brandCode && (
                      <p className="mt-1 text-sm text-red-600">{errors.brandCode}</p>
                    )}
                  </div>

                  {/* Category Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      카테고리
                    </label>
                    <div className="flex items-center min-h-[2.5rem]">
                      <p className="text-sm text-gray-500">
                        카테고리/그룹 목록 &gt; 상품 관리 에서 편집이 가능합니다.
                      </p>
                    </div>
                  </div>

                  {/* Product Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      상품 코드 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="itemCode"
                      value={formData.itemCode}
                      onChange={handleInputChange}
                      maxLength={20}
                      title="영문 대소문자, 숫자, 언더스코어(_), 하이픈(-)만 입력 가능"
                      className={`w-full px-3 py-2 border ${errors.itemCode ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="영문, 숫자, _, - 만 입력"
                    />
                    {errors.itemCode && (
                      <p className="mt-1 text-sm text-red-600">{errors.itemCode}</p>
                    )}
                  </div>

                  {/* Product Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      상품 상태 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="ACTIVE">활성</option>
                      <option value="INACTIVE">비활성</option>
                      <option value="PENDING">대기중</option>
                      <option value="ARCHIVED">보관됨</option>
                    </select>
                  </div>

                  {/* Product Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      상품명 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      maxLength={20}
                      className={`w-full px-3 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="상품명을 입력하세요"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                    )}
                  </div>

                  {/* Short Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      축약명
                    </label>
                    <input
                      type="text"
                      name="shortName"
                      value={formData.shortName}
                      onChange={handleInputChange}
                      maxLength={10}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="축약명을 입력하세요"
                    />
                  </div>

                  {/* POS Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      포스 상품코드
                    </label>
                    <input
                      type="text"
                      name="posCode"
                      value={formData.posCode}
                      onChange={handleInputChange}
                      maxLength={20}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="포스 상품코드를 입력하세요"
                    />
                  </div>

                  {/* Comment */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      짧은 소개
                    </label>
                    <input
                      type="text"
                      name="comment"
                      value={formData.comment}
                      onChange={handleInputChange}
                      maxLength={50}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="짧은 소개를 입력하세요"
                    />
                  </div>
                </div>
              </div>

              {/* Price Information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <DollarSign className="w-5 h-5 mr-2" />
                  가격 정보
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Original Price */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      상품 가격 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="originalPrice"
                      value={formData.originalPrice ?? ''}
                      onChange={handleInputChange}
                      min="0"
                      className={`w-full px-3 py-2 border ${errors.originalPrice ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]`}
                      placeholder="상품 가격을 입력하세요"
                    />
                    {errors.originalPrice && (
                      <p className="mt-1 text-sm text-red-600">{errors.originalPrice}</p>
                    )}
                  </div>

                  {/* Sale Price */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      판매 가격 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="salePrice"
                      value={formData.salePrice ?? ''}
                      onChange={handleInputChange}
                      min="0"
                      className={`w-full px-3 py-2 border ${errors.salePrice ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]`}
                      placeholder="판매 가격을 입력하세요"
                    />
                    {errors.salePrice && (
                      <p className="mt-1 text-sm text-red-600">{errors.salePrice}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Reward Information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Gift className="w-5 h-5 mr-2" />
                  적립 정보
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Reward Stamp Count */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      적립 스탬프 수 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="rewardStampCount"
                      value={formData.rewardStampCount ?? ''}
                      onChange={handleInputChange}
                      min="0"
                      className={`w-full px-3 py-2 border ${errors.rewardStampCount ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]`}
                      placeholder="적립 스탬프 수를 입력하세요"
                    />
                    {errors.rewardStampCount && (
                      <p className="mt-1 text-sm text-red-600">{errors.rewardStampCount}</p>
                    )}
                  </div>

                  {/* Reward Point Ratio */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      포인트 적립률 (%) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="rewardPointRatio"
                      value={formData.rewardPointRatio ?? ''}
                      onChange={handleInputChange}
                      min="0"
                      max="1"
                      className={`w-full px-3 py-2 border ${errors.rewardPointRatio ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]`}
                      placeholder="1.50 (1.5%)"
                    />
                    {errors.rewardPointRatio && (
                      <p className="mt-1 text-sm text-red-600">{errors.rewardPointRatio}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Service Options */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Settings className="w-5 h-5 mr-2" />
                  서비스 옵션
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Discount */}
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="discount"
                        checked={formData.discount === 1}
                        onChange={(e) => ProductFormUtils.handleCheckboxChange('discount', e.target.checked, setFormData, setErrors)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">
                        할인 가능 <span className="text-red-500">*</span>
                      </span>
                    </label>
                    {errors.discount && (
                      <p className="mt-1 text-sm text-red-600">{errors.discount}</p>
                    )}
                  </div>

                  {/* Voucher */}
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="voucher"
                        checked={formData.voucher === 1}
                        onChange={(e) => ProductFormUtils.handleCheckboxChange('voucher', e.target.checked, setFormData, setErrors)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">
                        교환권 가능 <span className="text-red-500">*</span>
                      </span>
                    </label>
                    {errors.voucher && (
                      <p className="mt-1 text-sm text-red-600">{errors.voucher}</p>
                    )}
                  </div>

                  {/* Reservation */}
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="reservation"
                        checked={formData.reservation === 1}
                        onChange={(e) => ProductFormUtils.handleCheckboxChange('reservation', e.target.checked, setFormData, setErrors)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">
                        예약 가능 <span className="text-red-500">*</span>
                      </span>
                    </label>
                    {errors.reservation && (
                      <p className="mt-1 text-sm text-red-600">{errors.reservation}</p>
                    )}
                  </div>

                  {/* Delivery */}
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="delivery"
                        checked={formData.delivery === 1}
                        onChange={(e) => ProductFormUtils.handleCheckboxChange('delivery', e.target.checked, setFormData, setErrors)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">
                        배달 가능 <span className="text-red-500">*</span>
                      </span>
                    </label>
                    {errors.delivery && (
                      <p className="mt-1 text-sm text-red-600">{errors.delivery}</p>
                    )}
                  </div>

                  {/* Shipping */}
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="shipping"
                        checked={formData.shipping === 1}
                        onChange={(e) => ProductFormUtils.handleCheckboxChange('shipping', e.target.checked, setFormData, setErrors)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">
                        배송 가능 <span className="text-red-500">*</span>
                      </span>
                    </label>
                    {errors.shipping && (
                      <p className="mt-1 text-sm text-red-600">{errors.shipping}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FileText className="w-5 h-5 mr-2" />
                  상품 설명
                </h2>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    상품 설명
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="상품에 대한 자세한 설명을 입력하세요"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between items-center">
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  초기화
                </button>
                
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => navigate('/products')}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center"
                  >
                    <Save className="w-5 h-5 mr-2" />
                    {isSubmitting ? '생성 중...' : '상품 생성'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ProductCreate;
